import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, MapPin, Clock, Users, Download, Search, Star, AlertCircle, MessageCircle, Lock, UserCheck, Briefcase } from 'lucide-react';
import WhatsAppModal from '../components/WhatsAppModal';  // Task 22
import CandidateAllocationModal from '../components/CandidateAllocationModal';
import { candidateAPI } from '../api/client';
import CallButtonMobile from '../components/mobile/CallButtonMobile';
import axiosInstance from '../config/axiosConfig';  // Task 27

export default function JDDetail() {
  const { jdId } = useParams();
  const navigate = useNavigate();
  const [jd, setJd] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [matching, setMatching] = useState(false);
  const [error, setError] = useState(null);
  const [matchingStatus, setMatchingStatus] = useState(null);
  const [matchingMessage, setMatchingMessage] = useState('');
  const [hasRunMatching, setHasRunMatching] = useState(false);
  const [matchingProfile, setMatchingProfile] = useState(null);
  const [sectors, setSectors] = useState([]);
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Task 22: WhatsApp modal state
  const [whatsappModalOpen, setWhatsappModalOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [allocationMatch, setAllocationMatch] = useState(null);

  useEffect(() => {
    setMatches([]);
    setMatchingStatus(null);
    setMatchingMessage('');
    setHasRunMatching(false);
    setError(null);
    fetchJD();
  }, [jdId]);

  const fetchJD = async () => {
    try {
      const response = await axiosInstance.get(`/jds/${jdId}`);
      setJd(response.data);
      const [profileResponse, sectorResponse] = await Promise.all([
        axiosInstance.get(`/jds/${jdId}/matching-profile`),
        axiosInstance.get('/taxonomy/sector'),
      ]);
      setMatchingProfile(profileResponse.data);
      setSectors(sectorResponse.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const saveMatchingProfile = async () => {
    if (!matchingProfile?.primary_sector_id) {
      setError('Select a primary sector before confirming matching setup.');
      return;
    }
    setSavingProfile(true);
    try {
      const response = await axiosInstance.put(`/jds/${jdId}/matching-profile`, matchingProfile);
      setMatchingProfile(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not save matching setup.');
    } finally {
      setSavingProfile(false);
    }
  };

  const runMatching = async () => {
    setMatching(true);
    setError(null);
    setMatchingStatus(null);
    setMatchingMessage('');
    try {
      const response = await axiosInstance.get(`/jds/match/${jdId}?limit=50`);
      setMatches(response.data.matches || []);
      setMatchingStatus(response.data.matching_status || null);
      setMatchingMessage(response.data.message || '');
      setHasRunMatching(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : detail?.message || err.message || 'Matching could not be completed.'
      );
      setHasRunMatching(true);
    } finally {
      setMatching(false);
    }
  };

  const getEmptyMatchCopy = () => {
    if (!hasRunMatching) {
      return {
        title: 'No matches yet',
        message: 'Click "Run Matching" to find suitable candidates for this job.'
      };
    }
    const titles = {
      jd_not_evaluable: 'JD needs more matching evidence',
      insufficient_candidate_evidence: 'Candidate evidence is incomplete',
      ambiguous_candidate_evidence: 'Candidate evidence is ambiguous',
      no_suitable_match: 'No suitable matches found'
    };
    return {
      title: titles[matchingStatus] || 'No suitable matches found',
      message: matchingMessage || 'No candidate demonstrated enough relevant evidence for this JD.'
    };
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-blue-600 bg-blue-100';
    return 'text-gray-600 bg-gray-100';
  };

  const getScoreIcon = (score) => {
    if (score >= 80) return <Star className="w-4 h-4 text-green-600" />;
    if (score >= 60) return <Star className="w-4 h-4 text-blue-600" />;
    return <AlertCircle className="w-4 h-4 text-gray-600" />;
  };

  const downloadJD = async () => {
    try {
      const response = await axiosInstance.get(`/jds/download/${jdId}`, {
        responseType: 'blob'
      });

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = response.headers?.['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
      a.download = filenameMatch?.[1] || `JD_${jdId}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Download failed: ' + err.message);
    }
  };
  
  // Task 22: Handle WhatsApp for matched candidates
  const handleWhatsApp = (match) => {
    // Convert match object to candidate format
    const candidate = {
      id: match.candidate_id,
      name: match.name,
      email: match.email,
      phone: match.phone,
      location: match.location
    };
    setSelectedCandidate(candidate);
    setWhatsappModalOpen(true);
  };

  const downloadCandidateResume = async (match) => {
    try {
      const response = await candidateAPI.downloadResume(match.candidate_id, Number(jdId));
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${match.candidate_reference || `CAND-${match.candidate_id}`}-resume`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (requestError) {
      const detail = requestError.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Resume download failed.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 rounded w-1/4 mb-6"></div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="h-6 bg-gray-300 rounded w-1/2 mb-4"></div>
              <div className="h-4 bg-gray-300 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-300 rounded w-3/4"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !jd) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
              <span className="text-red-700">Error: {error}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-3 sm:gap-4">
            <button
              onClick={() => navigate('/jd-upload')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Job Descriptions</span>
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Job Description Details</h1>
          </div>

          {jd?.file_path && (
            <button
              onClick={downloadJD}
              className="flex items-center space-x-2 bg-[#f59e0b] text-white px-4 py-2 rounded-lg hover:bg-[#d97706] transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Download JD</span>
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* JD Details */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center space-x-3 mb-4">
                <FileText className="w-6 h-6 text-[#14b8a6]" />
                <h2 className="text-xl font-semibold text-gray-900">{jd?.role_title}</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Client</p>
                  <p className="text-gray-900">{jd?.client_name}</p>
                </div>

                {jd?.experience_range && (
                  <div className="flex items-center space-x-2">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">Experience</p>
                      <p className="text-gray-900">{jd.experience_range}</p>
                    </div>
                  </div>
                )}

                {jd?.location && (
                  <div className="flex items-center space-x-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">Location</p>
                      <p className="text-gray-900">{jd.location}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-center space-x-2">
                  <Users className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Openings</p>
                    <p className="text-gray-900">{jd?.openings || 1}</p>
                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-500">Status</p>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    jd?.status === 'Active'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {jd?.status}
                  </span>
                </div>

                {jd?.assigned_to && jd.assigned_to.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-2">Assigned To</p>
                    <div className="space-y-1">
                      {jd.assigned_to.map(user => (
                        <div key={user.id} className="text-sm text-gray-900">
                          {user.name}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {jd?.manual_description && (
                  <div className="pt-4 border-t">
                    <p className="text-sm font-medium text-gray-500 mb-2">Description</p>
                    <p className="text-gray-700 text-sm whitespace-pre-wrap">{jd.manual_description}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Matching Results */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <Search className="w-6 h-6 text-[#14b8a6]" />
                  <h2 className="text-xl font-semibold text-gray-900">Candidate Matching</h2>
                </div>

                <button
                  onClick={runMatching}
                  disabled={matching}
                  className="bg-[#14b8a6] text-white px-4 py-2 rounded-lg hover:bg-[#0f766e] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {matching ? 'Matching...' : 'Run Matching'}
                </button>
              </div>

              {matchingProfile?.setup_status !== 'confirmed' && matchingProfile?.can_edit && (
                <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <h3 className="font-medium text-amber-900">Confirm matching setup</h3>
                  <p className="mt-1 text-sm text-amber-800">Review the suggested sector and add the skills that are mandatory for this JD. Matching remains advisory.</p>
                  <div className="mt-3 grid gap-3 md:grid-cols-2">
                    <select value={matchingProfile?.primary_sector_id || ''} onChange={(e) => setMatchingProfile({ ...matchingProfile, primary_sector_id: Number(e.target.value) })} className="rounded border border-amber-300 bg-white p-2 text-sm text-slate-900">
                      <option value="" className="bg-white text-slate-900">Select primary sector</option>
                      {sectors.map((sector) => <option key={sector.id} value={sector.id} className="bg-white text-slate-900">{sector.name}</option>)}
                    </select>
                    <input value={(matchingProfile?.required_skills || []).join(', ')} onChange={(e) => setMatchingProfile({ ...matchingProfile, required_skills: e.target.value.split(',').map((item) => item.trim()).filter(Boolean) })} placeholder="Mandatory skills, comma separated" className="jd-matching-keywords rounded border border-amber-300 bg-white p-2 text-sm text-slate-900 caret-slate-900 placeholder:text-slate-400 focus:text-slate-900" />
                  </div>
                  <div className="mt-3 flex flex-wrap gap-4 text-sm text-amber-900">
                    <label><input type="checkbox" checked={matchingProfile?.required_sector || false} onChange={(e) => setMatchingProfile({ ...matchingProfile, required_sector: e.target.checked })} /> Sector is mandatory</label>
                    <label><input type="checkbox" checked={matchingProfile?.required_experience || false} onChange={(e) => setMatchingProfile({ ...matchingProfile, required_experience: e.target.checked })} /> Experience is mandatory</label>
                    <label><input type="checkbox" checked={matchingProfile?.required_location || false} onChange={(e) => setMatchingProfile({ ...matchingProfile, required_location: e.target.checked })} /> Location is mandatory</label>
                  </div>
                  <button onClick={saveMatchingProfile} disabled={savingProfile} className="mt-3 rounded bg-amber-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50">{savingProfile ? 'Saving…' : 'Confirm matching setup'}</button>
                </div>
              )}

              {matchingProfile?.setup_status !== 'confirmed' && !matchingProfile?.can_edit && (
                <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  Matching setup is awaiting confirmation by the JD uploader or an authorised manager. You can still run advisory matching with the current setup.
                </div>
              )}

              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-center">
                    <AlertCircle className="w-4 h-4 text-red-500 mr-2" />
                    <span className="text-red-700 text-sm">{error}</span>
                  </div>
                </div>
              )}

              {matches.length === 0 && !matching && !error && (
                <div className="text-center py-12">
                  <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{getEmptyMatchCopy().title}</h3>
                  <p className="text-gray-500">{getEmptyMatchCopy().message}</p>
                </div>
              )}

              {matches.length > 0 && (
                <div className="space-y-4">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-sm text-gray-600">
                      Found {matches.length} potential matches
                    </p>
                    <div className="flex flex-wrap items-center gap-3 text-xs sm:gap-4">
                      <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 bg-green-100 rounded"></div>
                        <span>≥80% Excellent</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 bg-blue-100 rounded"></div>
                        <span>≥60% Good</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 bg-gray-100 rounded"></div>
                        <span>&lt;60% Low</span>
                      </div>
                    </div>
                  </div>

                  {matches.map((match, index) => (
                    <div key={match.candidate_id} className="border border-gray-200 rounded-lg p-4 hover:border-[#14b8a6] transition-colors">
                      <div className="mb-3 flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="mb-2 flex flex-wrap items-center gap-2">
                            <span className="font-medium text-gray-900">{match.display_name || match.name}</span>
                            <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${getScoreColor(match.overall_score)}`}>
                              {getScoreIcon(match.overall_score)}
                              <span className="ml-1">{match.overall_score}%</span>
                            </span>
                            {match.visibility === 'masked' && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800">
                                <Lock className="h-3 w-3" /> Masked profile
                              </span>
                            )}
                            {match.allocation?.status === 'active' && (
                              <span className="rounded-full bg-teal-100 px-2 py-1 text-xs font-medium text-teal-800">
                                Allocated to {match.allocation.recruiter?.name || 'recruiter'}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-600 space-y-1">
                            <div className="flex items-center gap-1">
                              <Briefcase className="h-3 w-3" />
                              <span>{match.role || 'Role not specified'}</span>
                            </div>
                            <div>Reference: {match.candidate_reference || match.candidate_id}</div>
                            <div>Primary owner: {match.primary_owner?.name || 'Unassigned'}</div>
                            {match.email && <div>{match.email}</div>}
                            {match.phone && <div>{match.phone}</div>}
                            {match.location && <div className="flex items-center space-x-1">
                              <MapPin className="w-3 h-3" />
                              <span>{match.location}</span>
                            </div>}
                            <div>
                              Experience: {match.candidate_experience_years == null ? 'Not specified' : `${match.candidate_experience_years} year(s)`}
                            </div>
                          </div>
                        </div>
                        <div className="text-right text-xs text-gray-500">
                          <div>#{index + 1}</div>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-4 text-xs">
                        <div className="text-center">
                          <div className="font-medium text-gray-900">{match.skills_score}%</div>
                          <div className="text-gray-500">Skills</div>
                        </div>
                        <div className="text-center">
                          <div className="font-medium text-gray-900">
                            {match.experience_applicable === false ? 'N/A' : `${match.experience_score}%`}
                          </div>
                          <div className="text-gray-500">Experience</div>
                        </div>
                        <div className="text-center">
                          <div className="font-medium text-gray-900">{match.location_score}%</div>
                          <div className="text-gray-500">Location</div>
                        </div>
                      </div>

                      {(match.matching_skills || []).length > 0 && (
                        <div className="mt-3">
                          <p className="text-xs text-gray-500 mb-1">Matching Skills:</p>
                          <div className="flex flex-wrap gap-1">
                            {match.matching_skills.map(skill => (
                              <span key={skill} className="inline-flex items-center px-2 py-1 text-xs bg-[#14b8a6]/10 text-[#14b8a6] rounded">
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {(match.warnings || []).length > 0 && (
                        <div className="mt-3 space-y-2">
                          {match.warnings.map((warning) => (
                            <div key={warning.type} className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                              <span>{warning.message}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {match.visibility === 'masked' && (
                        <p className="mt-3 text-xs text-gray-500">
                          Contact details, name, resume and identifying evidence remain hidden until a manager allocates this candidate for the JD.
                        </p>
                      )}
                      
                      <div className="mt-3 flex flex-wrap gap-2 border-t border-gray-100 pt-3">
                        {match.contact_allowed && match.phone && (
                          <CallButtonMobile
                            candidate={{
                              id: match.candidate_id,
                              name: match.display_name || match.name,
                              phone: match.phone,
                            }}
                            jdId={Number(jdId)}
                            size="small"
                          />
                        )}
                        {match.contact_allowed && match.phone && (
                          <button
                            onClick={() => handleWhatsApp(match)}
                            className="inline-flex items-center px-3 py-2 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors shadow-sm"
                          >
                            <MessageCircle className="w-4 h-4 mr-2" />
                            Send WhatsApp
                          </button>
                        )}
                        {match.contact_allowed && match.resume_available && (
                          <button
                            onClick={() => downloadCandidateResume(match)}
                            className="inline-flex items-center rounded-lg border border-[#14b8a6] px-3 py-2 text-sm text-[#0f766e] transition-colors hover:bg-[#14b8a6]/10"
                          >
                            <Download className="mr-2 h-4 w-4" />
                            Download resume
                          </button>
                        )}
                        {match.can_manage_allocation && (
                          <button
                            onClick={() => setAllocationMatch(match)}
                            className="inline-flex items-center rounded-lg bg-[#14b8a6] px-3 py-2 text-sm text-white shadow-sm transition-colors hover:bg-[#0f766e]"
                          >
                            <UserCheck className="mr-2 h-4 w-4" />
                            {match.allocation?.status === 'active' ? 'Manage allocation' : 'Allocate for this JD'}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Task 22: WhatsApp Modal */}
      <WhatsAppModal
        isOpen={whatsappModalOpen}
        onClose={() => {
          setWhatsappModalOpen(false);
          setSelectedCandidate(null);
        }}
        candidate={selectedCandidate}
        jd={jd}
      />
      <CandidateAllocationModal
        isOpen={!!allocationMatch}
        onClose={() => setAllocationMatch(null)}
        match={allocationMatch}
        jd={jd}
        onSuccess={runMatching}
      />
    </div>
  );
}
